import React from "react";
import {
  MessageSquareDashed,
  MessageSquareOff,
  MessageSquareWarning,
  type LucideIcon,
  MessageSquareCheck,
} from "lucide-react";
import { Tooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { Review } from "@/services/bedrock";
import { ReviewStatusBadge } from "./ReviewStatusBadge";
import { REVIEW_TONE_CLASSES, SOURCE_SYSTEM_CONFIG, type ReviewTone, getInternalBadgeName } from "./utils";

type ReplyStatusProps = {
  review: Review;
  variant?: "icon" | "badge";
  /** When true, internal reviews show Replied/Skipped/Unreplied instead of internal source label. */
  resolveInternalStatus?: boolean;
};

function getReplyStatus(
  review: Review,
  resolveInternalStatus = false
): { label: string; Icon: LucideIcon | null; tone: ReviewTone; hidden?: boolean } {
  if (review.replied) {
    return { label: "Replied", Icon: MessageSquareCheck, tone: "positive" };
  }
  const isInternal = SOURCE_SYSTEM_CONFIG[review.sourceSystem]?.isInternal;
  if (isInternal && !resolveInternalStatus) {
    return {
      label: getInternalBadgeName(review),
      Icon: null,
      tone: "subtle",
    };
  }
  if (review.replySkipped) {
    return { hidden: !resolveInternalStatus, label: "Skipped", Icon: MessageSquareOff, tone: "muted" };
  }
  if (isInternal && resolveInternalStatus) {
    return { label: "Unreplied", Icon: MessageSquareDashed, tone: "subtle" };
  }
  return { label: "Needs Reply", Icon: MessageSquareWarning, tone: "caution" };
}

export function ReplyStatus({ review, variant = "badge", resolveInternalStatus = false }: ReplyStatusProps) {
  const { label, Icon, tone, hidden } = getReplyStatus(review, resolveInternalStatus);

  if (hidden) return null;

  if (Icon && variant === "icon") {
    return (
      <Tooltip>
        <Tooltip.Trigger
          render={<Icon className={cn("size-4", REVIEW_TONE_CLASSES[tone].icon)} aria-label={label} />}
        />
        <Tooltip.Content>{label}</Tooltip.Content>
      </Tooltip>
    );
  }

  return <ReviewStatusBadge label={label} icon={Icon} tone={tone} />;
}
